async function json(path, opts = {}) {
  const r = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  if (r.status === 401) {
    window.location.href = '/';
    return;
  }
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

export const api = {
  me: () => json('/auth/me'),
  logout: () => json('/auth/logout', { method: 'POST' }),
  guild: (gid) => json(`/api/guilds/${gid}`),
  guildStats: (gid) => json(`/api/guilds/${gid}/stats`),
  guildRecent: (gid, limit = 15) => json(`/api/guilds/${gid}/recent?limit=${limit}`),
  guildRoles: (gid) => json(`/api/guilds/${gid}/roles`),
  userInfo: (gid, uid) => json(`/api/guilds/${gid}/users/${uid}`),
  getConfig: (gid) => json(`/api/guilds/${gid}/config`),
  putConfig: (gid, body) => json(`/api/guilds/${gid}/config`, { method: 'PUT', body: JSON.stringify(body) }),
  modActions: (gid, params = '') => json(`/api/guilds/${gid}/moderation/actions${params}`),
  warnings: (gid, params = '') => json(`/api/guilds/${gid}/moderation/warnings${params}`),
  memberEvents: (gid, params = '') => json(`/api/guilds/${gid}/moderation/events${params}`),
  members: (gid, params = '') => json(`/api/guilds/${gid}/members${params}`),
  userDetail: (gid, uid) => json(`/api/guilds/${gid}/moderation/users/${uid}`),
  warn: (gid, body) => json(`/api/guilds/${gid}/moderation/warn`, { method: 'POST', body: JSON.stringify(body) }),
  deleteWarning: (gid, id) => json(`/api/guilds/${gid}/moderation/warnings/${id}`, { method: 'DELETE' }),
  timeout: (gid, body) => json(`/api/guilds/${gid}/moderation/timeout`, { method: 'POST', body: JSON.stringify(body) }),
  untimeout: (gid, body) => json(`/api/guilds/${gid}/moderation/untimeout`, { method: 'POST', body: JSON.stringify(body) }),
  kick: (gid, body) => json(`/api/guilds/${gid}/moderation/kick`, { method: 'POST', body: JSON.stringify(body) }),
  ban: (gid, body) => json(`/api/guilds/${gid}/moderation/ban`, { method: 'POST', body: JSON.stringify(body) }),
  unban: (gid, body) => json(`/api/guilds/${gid}/moderation/unban`, { method: 'POST', body: JSON.stringify(body) }),
  spamHammer: (gid, body) => json(`/api/guilds/${gid}/moderation/spam-hammer`, { method: 'POST', body: JSON.stringify(body) }),
  cleanup: (gid, body) => json(`/api/guilds/${gid}/moderation/cleanup`, { method: 'POST', body: JSON.stringify(body) }),

  uploadWelcomeBg: async (gid, file) => {
    const fd = new FormData();
    fd.append('background', file);
    const r = await fetch(`/api/guilds/${gid}/welcome-card/background`, { method: 'POST', body: fd, credentials: 'include' });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    return r.json();
  },
  deleteWelcomeBg: (gid) => json(`/api/guilds/${gid}/welcome-card/background`, { method: 'DELETE' }),
  welcomeBgUrl: (gid) => `/api/guilds/${gid}/welcome-card/background?t=${Date.now()}`,

  invites: (gid) => json(`/api/guilds/${gid}/invites`),
  invitedBy: (gid, uid) => json(`/api/guilds/${gid}/invites/by/${uid}`),
  inviteesOf: (gid, uid) => json(`/api/guilds/${gid}/invites/from/${uid}`),
  activeInvites: (gid) => json(`/api/guilds/${gid}/invites/active`),
  revokeInvite: (gid, code) => json(`/api/guilds/${gid}/invites/code/${code}`, { method: 'DELETE' }),

  listReactionRoles: (gid) => json(`/api/guilds/${gid}/reaction-roles`),
  getReactionRole: (gid, id) => json(`/api/guilds/${gid}/reaction-roles/${id}`),
  createReactionRole: (gid, body) => json(`/api/guilds/${gid}/reaction-roles`, { method: 'POST', body: JSON.stringify(body) }),
  updateReactionRole: (gid, id, body) => json(`/api/guilds/${gid}/reaction-roles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteReactionRole: (gid, id) => json(`/api/guilds/${gid}/reaction-roles/${id}`, { method: 'DELETE' }),
  publishReactionRole: (gid, id) => json(`/api/guilds/${gid}/reaction-roles/${id}/publish`, { method: 'POST' }),

  botDms: (gid, params = '') => json(`/api/guilds/${gid}/bot-dms${params}`),

  listChannelMods: (gid) => json(`/api/guilds/${gid}/channel-mods`),
  addChannelMod: (gid, body) => json(`/api/guilds/${gid}/channel-mods`, { method: 'POST', body: JSON.stringify(body) }),
  removeChannelMod: (gid, body) => json(`/api/guilds/${gid}/channel-mods`, { method: 'DELETE', body: JSON.stringify(body) }),
  releaseChannelJail: (gid, jailId) => json(`/api/guilds/${gid}/channel-mods/jails/${jailId}/release`, { method: 'POST' }),

  restrictions: (gid) => json(`/api/guilds/${gid}/restrictions`),

  listTickets: (gid, status) => json(`/api/guilds/${gid}/tickets${status ? `?status=${status}` : ''}`),
  closeTicket: (gid, id, reason) => json(`/api/guilds/${gid}/tickets/${id}/close`, { method: 'POST', body: JSON.stringify({ reason }) }),

  auditSearch: (gid, params) => {
    const qs = Object.entries(params || {})
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    return json(`/api/guilds/${gid}/audit${qs ? `?${qs}` : ''}`);
  },
  auditActions: (gid) => json(`/api/guilds/${gid}/audit/actions`),

  listPolls: (gid) => json(`/api/guilds/${gid}/polls`),
  getPoll: (gid, id) => json(`/api/guilds/${gid}/polls/${id}`),
  closePoll: (gid, id) => json(`/api/guilds/${gid}/polls/${id}/close`, { method: 'POST' }),

  ownerGuilds: () => json('/api/owner/guilds'),
  ownerLeave: (gid, body) => json(`/api/owner/guilds/${gid}/leave`, { method: 'POST', body: JSON.stringify(body || {}) }),
  ownerBlacklist: (body) => json('/api/owner/blacklist', { method: 'POST', body: JSON.stringify(body) }),
  ownerUnblacklist: (gid) => json(`/api/owner/blacklist/${gid}`, { method: 'DELETE' }),
};

export function avatarUrl(user) {
  if (!user) return '';
  if (user.avatar) return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=64`;
  return `https://cdn.discordapp.com/embed/avatars/${(BigInt(user.id) >> 22n) % 6n}.png`;
}

export function guildIconUrl(guild) {
  if (!guild?.icon) return null;
  return `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png?size=128`;
}

export function timeAgo(epochSeconds) {
  const diff = Date.now() / 1000 - epochSeconds;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(epochSeconds * 1000).toLocaleDateString();
}

export function formatDuration(ms) {
  if (!ms) return 'n/a';
  const units = [['w', 604_800_000], ['d', 86_400_000], ['h', 3_600_000], ['m', 60_000], ['s', 1_000]];
  let r = ms; const parts = [];
  for (const [l, s] of units) { const n = Math.floor(r / s); if (n > 0) { parts.push(`${n}${l}`); r -= n * s; } }
  return parts.length ? parts.join(' ') : '0s';
}
